import React, { useEffect, useState } from 'react';
import { Building2, Copy, LifeBuoy, Mail, MessageCircle, ScreenShare, Send } from 'lucide-react';
import { Modal } from './Modal';
import { PresenceDot } from './PresenceDot';
import { User, UserProfileInfo, PresenceStatus, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { STATUS_LABEL } from '../utils/presence';
import { dbService } from '../services/dbService';
import { openMailto } from '../utils/openExternal';
import { useToast } from '../contexts/ToastContext';
import { askRemoteSupport, requestRemoteSupport, useRemoteSupportMe } from '../services/remoteSupportService';

interface UserProfileModalProps {
  /** 시드 값(이름·아바타·소속). null이면 닫힌 상태다 */
  user: User | null;
  /** 실시간 접속 상태 — App이 onlineUsers에서 계산해 넘긴다(목록에 없으면 offline) */
  status: PresenceStatus;
  onClose: () => void;
  /** 없으면 '1:1 대화' 버튼을 숨긴다(지금 보고 있는 그 사람과의 1:1 대화방에서 연 경우) */
  onStartDm?: (user: User) => void;
  /** 나 자신에게는 원격지원 버튼을 띄우지 않는다 */
  currentUserId?: string;
  /** 원격지원 버튼을 둘 수 있는 배치인가(모바일 웹 배치에서는 숨긴다 — 뷰어가 PC 화면용이다) */
  allowRemoteSupport?: boolean;
}

type LoadState = 'loading' | 'ok' | 'error';

/**
 * 상대방 프로필 — 아바타·접속 상태·이름·소속·메일 주소 + '1:1 대화'.
 * 진입점(말풍선 아바타·1:1 대화방 헤더·멤버 목록·조직도·동료 검색)이 넘긴 값으로 즉시 그리고,
 * 소속·메일은 GET /api/users/:id/profile 응답으로 채운다. 캐시하지 않는다(클릭할 때만 도는 경로).
 */
export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  user,
  status,
  onClose,
  onStartDm,
  currentUserId,
  allowRemoteSupport = false,
}) => {
  const { showToast } = useToast();
  const remoteSupport = useRemoteSupportMe();
  const [profile, setProfile] = useState<UserProfileInfo | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const userId = user ? String(user.id) : null;

  useEffect(() => {
    if (!userId) return;
    // A를 열었다가 곧바로 B를 열면 A의 응답이 늦게 도착할 수 있다 — 버린다
    let ignore = false;
    setProfile(null);
    setLoadState('loading');
    dbService.getUserProfile(userId)
      .then((p) => {
        if (ignore) return;
        setProfile(p);
        setLoadState('ok');
      })
      .catch((err) => {
        if (ignore) return;
        console.error('[Profile] Failed to load profile:', err);
        setLoadState('error');
      });
    return () => { ignore = true; };
  }, [userId]);

  if (!user) return null;

  const name = profile?.name || user.username;
  const avatar = profile?.avatar || user.avatar;
  const depart = profile?.depart ?? user.depart ?? null;
  const affiliation = [profile?.affiliateLabel, depart].filter(Boolean).join(' · ');
  const email = profile?.email ?? null;

  const copyEmail = () => {
    if (!email) return;
    const failed = () =>
      showToast({ type: 'error', message: '메일 주소를 복사하지 못했습니다. 주소를 직접 선택해 복사해 주세요.' });
    const p = navigator.clipboard?.writeText(email);
    if (!p) { failed(); return; }
    p.then(() => showToast({ type: 'success', message: '메일 주소를 복사했습니다.' })).catch(failed);
  };

  const composeEmail = async () => {
    if (!email) return;
    const ok = await openMailto(email);
    if (!ok) showToast({ type: 'error', message: '메일 앱을 열지 못했습니다. 주소를 복사해 메일을 보내 주세요.' });
  };

  // 원격지원 버튼 2종 — 정책이 켜져 있을 때만, 나 자신에게는 없다.
  //  · 원격지원 시작: 내가 담당자(명단 또는 super_admin)일 때 — 상대 PC 화면을 보러 간다(상대 PC에 동의 창)
  //  · 원격지원 요청: 상대가 담당자일 때(프로필 응답) — 내 PC 화면을 봐 달라고 청한다(PC 앱에서만 실제로 나간다)
  // 상대가 오프라인이면 숨기지 않고 비활성 + 안내 — 통째로 숨기면 "버튼이 어디 있느냐"는 문의가 된다.
  // 상대가 원격지원을 주고받을 수 있는 기기에 접속해 있는지는 서버가 판정한다.
  const notSelf = String(user.id) !== String(currentUserId ?? '');
  const remoteOn = allowRemoteSupport && remoteSupport.enabled && notSelf;
  const canStart = remoteOn && remoteSupport.isHelpdesk;
  const canAsk = remoteOn && profile?.remoteSupporter === true;
  const targetOffline = status === 'offline';

  const remoteTarget = () => ({
    userId: String(user.id),
    name,
    avatar,
    depart,
    // 계열사끼리 사번이 겹칠 수 있어 프로필 API가 찾아 준 계열사를 함께 보낸다
    affiliateId: profile?.affiliateId ?? null,
  });

  const startRemoteSupport = () => {
    requestRemoteSupport(remoteTarget());
    onClose();
  };

  const askForRemoteSupport = () => {
    // 화면을 내줄 수 있는 곳은 PC 앱뿐이다 — 웹에는 듣는 쪽(RemoteSupportHost)이 없다
    if (!askRemoteSupport(remoteTarget())) {
      showToast({ type: 'info', message: '원격지원은 PC 앱에서 요청할 수 있습니다. PC 앱을 켜고 다시 요청해 주세요.' });
      return;
    }
    onClose();
  };

  const remoteButtonClass =
    'flex-1 min-w-[8.5rem] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const skeleton = <span className="block h-4 w-32 rounded bg-slate-100 animate-pulse" aria-hidden="true" />;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="프로필"
      size="sm"
      footer={onStartDm || canStart || canAsk ? (
        <div>
          <div className="flex flex-wrap gap-2">
            {onStartDm && (
              <button
                type="button"
                // 서버 아바타를 실어 보낸다 — 조직도 행처럼 시드 아바타가 빈 경우에도 초안 DM 헤더에 사진이 뜬다
                onClick={() => onStartDm({ ...user, avatar })}
                className="flex-1 min-w-[8.5rem] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white transition-colors"
              >
                <MessageCircle size={18} aria-hidden="true" />
                1:1 대화
              </button>
            )}
            {canStart && (
              <button
                type="button"
                onClick={startRemoteSupport}
                disabled={loadState === 'loading' || targetOffline}
                title="상대 PC 화면을 봅니다. 상대가 허용해야 시작됩니다"
                className={remoteButtonClass}
              >
                <ScreenShare size={18} aria-hidden="true" />
                원격지원 시작
              </button>
            )}
            {canAsk && (
              <button
                type="button"
                onClick={askForRemoteSupport}
                disabled={targetOffline}
                title="담당자에게 내 PC 화면을 봐 달라고 요청합니다"
                className={remoteButtonClass}
              >
                <LifeBuoy size={18} aria-hidden="true" />
                원격지원 요청
              </button>
            )}
          </div>
          {(canStart || canAsk) && targetOffline && (
            <p className="mt-2 text-xs text-slate-500 text-center">상대가 접속 중일 때만 원격지원을 쓸 수 있습니다</p>
          )}
        </div>
      ) : undefined}
    >
      <div className="flex flex-col items-center text-center pt-2 pb-4">
        <span className="relative">
          <img
            src={resolveAvatarUrl(avatar)}
            onError={handleAvatarError}
            alt=""
            className="w-20 h-20 rounded-full object-cover bg-slate-100"
          />
          <PresenceDot
            status={status}
            labelled
            className="absolute bottom-0.5 right-0.5 w-4 h-4 border-[3px] border-white"
          />
        </span>
        <p className="mt-3 text-lg font-semibold text-slate-800 break-anywhere">{name}</p>
        <p className="text-xs text-slate-500">{STATUS_LABEL[status]}</p>
      </div>

      <dl className="border-t border-slate-100 pt-3 space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <dt className="shrink-0 pt-0.5 text-slate-400">
            <Building2 size={18} aria-hidden="true" />
            <span className="sr-only">소속</span>
          </dt>
          <dd className="min-w-0 flex-1 text-slate-700">
            {affiliation ? (
              <span className="break-anywhere">{affiliation}</span>
            ) : loadState === 'loading' ? (
              skeleton
            ) : (
              <span className="text-slate-400">
                {loadState === 'error' ? '정보를 불러오지 못했습니다' : '소속 정보가 없습니다'}
              </span>
            )}
          </dd>
        </div>

        <div className="flex items-start gap-3">
          <dt className="shrink-0 pt-0.5 text-slate-400">
            <Mail size={18} aria-hidden="true" />
            <span className="sr-only">메일</span>
          </dt>
          <dd className="min-w-0 flex-1 text-slate-700">
            {loadState === 'loading' ? (
              skeleton
            ) : email ? (
              <div className="flex items-center gap-1">
                <span className="min-w-0 flex-1 break-all select-text">{email}</span>
                <button
                  type="button"
                  onClick={copyEmail}
                  aria-label="메일 주소 복사"
                  title="복사"
                  className="shrink-0 p-1.5 text-slate-500 hover:text-brand-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Copy size={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={composeEmail}
                  aria-label="메일 쓰기"
                  title="메일 쓰기"
                  className="shrink-0 p-1.5 text-slate-500 hover:text-brand-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Send size={16} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <span className="text-slate-400">
                {loadState === 'error' ? '정보를 불러오지 못했습니다' : '등록된 메일 주소가 없습니다'}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </Modal>
  );
};
